import { memo, RefObject } from 'react'
import { useSelector } from 'react-redux'
import { RenderProperty } from 'shared/renderProperties'
import { DEFAULT_EAGER_IMAGES, LOADING_TYPE } from 'client/constants'
import { getMarket, getQuantitySelector, stringRenderPropertySelector, getViewId } from 'client/store/config'
import { productOptionsByProductKeySelector } from 'client/store/productOptionsByProductKey'
import { GalleryTileEntityProvider } from 'client/components/Gallery/PreviewArea/GalleryTileEntityProvider'
import { AnalyticsNames, ANALYTICS_EVENT_ACTIONS, PREVIEW_TYPE, ANALYTICS_LABEL } from 'shared/constants'
import { useAnalytics } from 'client/hooks/gallery/useAnalytics'
import { designByIdSelector } from 'client/store/design'
import { getEntityImpressionId } from '~/client/store/analytics/entityImpressionId'
import { generateImpressionId } from '~/client/store/analytics/reducer'
import { DesignTile } from '~/client/components/Gallery/DesignTile'
import { designPersonalizationContextIdSelector } from '~/client/store/designPersonalization/selectors'
import { useFavoriteId } from '~/client/hooks/useFavoriteId'

interface PropTypes {
  entityId: string
  maxColorSwatchesPerRow: number
  refValue: RefObject<HTMLDivElement> | null
}

const DesignTileWrapperComponent = ({ entityId, maxColorSwatchesPerRow, refValue }: PropTypes) => {
  const analytics = useAnalytics()
  const designById = useSelector(designByIdSelector)
  const productOptionsByProductKey = useSelector(productOptionsByProductKeySelector)
  const stringRenderProperty = useSelector(stringRenderPropertySelector)
  const market = useSelector(getMarket)
  const quantity = useSelector(getQuantitySelector)
  const favoriteId = useFavoriteId(entityId)
  const viewId = useSelector(getViewId)
  const dpcId = useSelector(designPersonalizationContextIdSelector)

  const pricingPresentationType = stringRenderProperty(RenderProperty.PricingPresentationType)

  const curryHandleOpenQuickView =
    (colorSwatchObjects: Gallery.ContentQuery.ColorSwatch[], currentDesignId: string, tileEntity: State.TileEntity) => (): void => {
      const color = designById(currentDesignId).color?.split('#')[1]
      const impressionIdFactory = (id: string): string => {
        const tileImpressionId = getEntityImpressionId(id)

        return generateImpressionId(viewId, tileImpressionId)
      }

      const isPersonalized = dpcId ? 'Quick view opened with personalized design' : ''

      analytics.trackEvent({
        action: ANALYTICS_EVENT_ACTIONS.GALLERY_DESIGN_ENGAGEMENT,
        eventLabel: ANALYTICS_LABEL.FLY_OUT_VIEWED,
        eventDetail: isPersonalized,
        ...analytics.buildDesignEngagement({
          engagementAction: ANALYTICS_LABEL.OPEN_QUICKVIEW,
          selectedDesign: currentDesignId,
          color,
          tileEntity,
          colorSwatchObjects,
          impressionId: impressionIdFactory,
          location: AnalyticsNames.DesignTile,
          position: tileEntity.position,
        }),
      })
    }

  return (
    <GalleryTileEntityProvider tileEntityId={entityId}>
      {(
        tileEntity,
        imageProps,
        handleColorSwatchChange,
        colorSwatchObjects,
        currentColorSwatch,
        currentDesignId,
        greetingOptions,
        greetingTitle,
        shouldShowGreetingOptions,
        shouldHideForSocialMedia,
        isPreviouslyOrdered,
        onMouseEnter,
      ) => (
        <DesignTile
          noFollow
          colorSwatches={colorSwatchObjects}
          currentDesignId={currentDesignId}
          entityId={entityId}
          favoriteId={favoriteId}
          greetingOptions={greetingOptions}
          greetingTitle={greetingTitle}
          handleColorSwatchChange={handleColorSwatchChange}
          imageProps={{
            ...imageProps,
            loading: tileEntity.position < DEFAULT_EAGER_IMAGES ? LOADING_TYPE.EAGER : LOADING_TYPE.LAZY,
          }}
          isPreviouslyOrdered={isPreviouslyOrdered}
          market={market}
          maxColorSwatchesPerRow={maxColorSwatchesPerRow}
          previewType={PREVIEW_TYPE.TILE}
          pricingPresentationType={pricingPresentationType}
          productOptionsByProductKey={productOptionsByProductKey}
          quantity={quantity}
          refValue={refValue}
          selectedDesignId={currentColorSwatch.designId}
          shouldHideForSocialMedia={shouldHideForSocialMedia}
          shouldShowGreetingOptions={shouldShowGreetingOptions}
          tileEntity={tileEntity}
          onLabelClick={curryHandleOpenQuickView(colorSwatchObjects, currentDesignId, tileEntity)}
          onMouseEnter={onMouseEnter}
        />
      )}
    </GalleryTileEntityProvider>
  )
}

export const DesignTileWrapper = memo(DesignTileWrapperComponent)
